import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { FileChips, trFold, SearchClear } from "@/components/shared";
import { Award, LogOut, Search, Eye, Clock, Undo2, ListChecks, Pencil, Factory } from "lucide-react";
import { isImpersonating, endImpersonation } from "@/lib/impersonate";
import { PROGRESS_ITEMS, progressPct } from "@/pages/AssignerPage";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const zoneCls = {
  "Blue Zone": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "Green Zone": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};

const StandDateEdit = ({ s, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const save = () => {
    setEditing(false);
    const t = val.trim();
    if (t !== (s.kurulum_tarihi || "")) onSave(s, t);
  };
  if (editing) {
    return (
      <Input autoFocus value={val} onChange={(e) => setVal(e.target.value)} placeholder="12.10.2027"
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        onBlur={save} data-testid={`stand-date-input-${s.id}`} className="rounded-none h-7 w-[104px] text-xs bg-white" />
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className={s.kurulum_tarihi ? "font-semibold tabular-nums text-slate-700" : "text-slate-300"} data-testid={`stand-date-${s.id}`}>
        {s.kurulum_tarihi || "Kurulum tarihi yok"}
      </span>
      <button type="button" onClick={() => { setVal(s.kurulum_tarihi || ""); setEditing(true); }} data-testid={`stand-date-edit-${s.id}`} title="Kurulum tarihini düzenle" className="text-slate-300 hover:text-slate-500 transition-colors duration-150">
        <Pencil size={11} />
      </button>
    </span>
  );
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-");

const TABS = [
  { key: "all", label: "Tümü", pred: () => true },
  { key: "active", label: "Aktif", pred: (r) => r.status === "active" },
  { key: "closed", label: "Kapandı", pred: (r) => r.status === "closed" },
  { key: "won", label: "Kazananlar", pred: (r) => r.status === "won" },
];

const StatusBadge = ({ r }) => {
  if (r.status === "won")
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 bg-emerald-500/10 text-emerald-700 border border-emerald-500/20">Sonuçlandı</span>;
  if (r.status === "active")
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 bg-blue-500/10 text-blue-700 border border-blue-500/20"><Clock size={10} /> Aktif İhale</span>;
  if (r.status === "closed")
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 bg-slate-500/10 text-slate-600 border border-slate-500/20">Kapandı</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 bg-amber-500/10 text-amber-700 border border-amber-500/20">İhalesiz</span>;
};

export default function ViewerPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [zoneFilter, setZoneFilter] = useState("");
  const [isCm, setIsCm] = useState(false);
  const [stands, setStands] = useState([]);
  const [progressFor, setProgressFor] = useState(null);

  useEffect(() => {
    api.get("/viewer/projects").then((r) => { setRows(r.data); setLoaded(true); }).catch(() => setLoaded(true));
    api.get("/viewer/my-stands").then((r) => { setIsCm(r.data.is_cm); setStands(r.data.stands); }).catch(() => {});
  }, []);

  const patchStand = async (s, payload, revert) => {
    try {
      await api.patch(`/viewer/my-stands/${s.id}`, payload);
    } catch (e) {
      toast.error(apiError(e));
      revert?.();
    }
  };

  const toggleProgress = (s, item) => {
    const cur = s.kurulum_progress || [];
    const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
    const ordered = PROGRESS_ITEMS.filter((x) => next.includes(x));
    const apply = (val) => {
      setStands((xs) => xs.map((x) => (x.id === s.id ? { ...x, kurulum_progress: val } : x)));
      setProgressFor((pf) => (pf && pf.id === s.id ? { ...pf, kurulum_progress: val } : pf));
    };
    apply(ordered);
    patchStand(s, { kurulum_progress: ordered }, () => apply(cur));
  };

  const saveStandDate = (s, t) => {
    const prev = s.kurulum_tarihi || "";
    setStands((xs) => xs.map((x) => (x.id === s.id ? { ...x, kurulum_tarihi: t } : x)));
    patchStand(s, { kurulum_tarihi: t }, () => setStands((xs) => xs.map((x) => (x.id === s.id ? { ...x, kurulum_tarihi: prev } : x))));
  };

  const zones = useMemo(() => [...new Set(rows.map((r) => r.zone).filter((z) => z && z !== "-"))], [rows]);
  const term = trFold(q.trim());
  const searched = rows.filter(
    (r) =>
      (!term || [r.name, r.winner_company, r.zone, r.stand_no, r.project_type].some((v) => trFold(v).includes(term))) &&
      (!zoneFilter || r.zone === zoneFilter)
  );
  const activeTab = TABS.find((t) => t.key === tab);
  const visible = searched.filter(activeTab.pred);

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="viewer-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><Eye size={10} /> İzleyici</div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:block" data-testid="viewer-name">{user?.contact_name}</span>
            {isImpersonating() && (
              <button onClick={endImpersonation} data-testid="viewer-end-impersonation" className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors duration-200">
                <Undo2 size={14} /> Yönetici Hesabına Dön
              </button>
            )}
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="viewer-logout" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
              <LogOut size={14} /> Çıkış Yap
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {isCm && (
          <section className="space-y-3" data-testid="viewer-my-stands">
            <div>
              <h2 className="font-heading text-2xl font-extrabold tracking-tight flex items-center gap-2">
                <ListChecks size={20} className="text-blue-600" /> Standlarım
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">Size atanmış {stands.length} stand — kurulum ilerlemesini işaretleyebilir, kurulum tarihini girebilirsiniz</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {stands.map((s) => (
                <div key={s.id} className="bg-white border border-slate-200 p-4" data-testid={`stand-card-${s.id}`}>
                  <div className="font-semibold text-slate-800 text-sm">{s.name}</div>
                  <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-slate-400 flex-wrap">
                    {s.zone && <span className={`px-1.5 py-0.5 border ${zoneCls[s.zone] || "border-slate-200"}`}>{s.zone}</span>}
                    {s.stand_no && <span>Stand: {s.stand_no}</span>}
                    {s.area_m2 > 0 && <span>{s.area_m2} m²</span>}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                    <StandDateEdit s={s} onSave={saveStandDate} />
                    {s.producer_name && <span className="inline-flex items-center gap-1 text-slate-500"><Factory size={11} /> {s.producer_name}</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => setProgressFor(s)}
                    data-testid={`stand-progress-btn-${s.id}`}
                    title="Kurulum ilerlemesini işaretle"
                    className="mt-3 w-full flex items-center gap-2 group/sp"
                  >
                    <div className="flex-1 h-1.5 bg-slate-100 overflow-hidden">
                      <div className={`h-full transition-all duration-300 ${progressPct(s) === 100 ? "bg-emerald-500" : progressPct(s) > 0 ? "bg-blue-500" : ""}`} style={{ width: `${progressPct(s)}%` }} />
                    </div>
                    <span className={`text-xs font-semibold tabular-nums ${progressPct(s) === 100 ? "text-emerald-600" : progressPct(s) > 0 ? "text-slate-700" : "text-slate-300"}`}>%{progressPct(s)}</span>
                    <ListChecks size={13} className="text-slate-300 group-hover/sp:text-slate-600 transition-colors duration-150" />
                  </button>
                </div>
              ))}
              {stands.length === 0 && (
                <div className="col-span-full bg-white border border-slate-200 px-4 py-8 text-center text-slate-400 text-sm">Size atanmış stand bulunamadı.</div>
              )}
            </div>
          </section>
        )}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight flex items-center gap-2"><Award size={24} className="text-emerald-600" /> Projeler</h1>
            <p className="text-sm text-slate-500 mt-1">{isCm ? "Size atanmış projelerin ihale durumları" : "İhale durumları ve kazanan firmalar"} — {rows.length} proje</p>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proje, firma, stand ara..." data-testid="viewer-search" className="rounded-none border-slate-200 h-10 pl-9 pr-9 w-64 bg-white" />
            <SearchClear value={q} onClear={() => setQ("")} testid="viewer-search-clear" />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex gap-0 border-b border-slate-200 overflow-x-auto" data-testid="viewer-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`viewer-tab-${t.key}`}
                className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 whitespace-nowrap transition-colors duration-200 ${
                  tab === t.key
                    ? t.key === "won" ? "border-emerald-600 text-emerald-700" : "border-slate-900 text-slate-900"
                    : "border-transparent text-slate-400 hover:text-slate-600"
                }`}
              >
                {t.label} ({searched.filter(t.pred).length})
              </button>
            ))}
          </div>
          {zones.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="viewer-zone-filter">
              <button
                onClick={() => setZoneFilter("")}
                data-testid="viewer-zone-all"
                className={`px-3 py-1.5 text-xs font-semibold border transition-colors duration-200 ${!zoneFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
              >
                Tüm Bölgeler
              </button>
              {zones.map((z) => (
                <button
                  key={z}
                  onClick={() => setZoneFilter(zoneFilter === z ? "" : z)}
                  data-testid={`viewer-zone-${z}`}
                  className={`px-3 py-1.5 text-xs font-semibold border transition-colors duration-200 ${zoneFilter === z ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
                >
                  {z} ({rows.filter((r) => r.zone === z).length})
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3 font-medium">Proje</th>
                <th className="px-4 py-3 font-medium">Bölge</th>
                <th className="px-4 py-3 font-medium">Stand</th>
                <th className="px-4 py-3 font-medium">Tür</th>
                <th className="px-4 py-3 font-medium">Durum</th>
                <th className="px-4 py-3 font-medium">Dosyalar</th>
                <th className="px-4 py-3 font-medium">Kazanan Firma</th>
                <th className="px-4 py-3 font-medium">Tarih</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-400" data-testid="viewer-empty">
                    {!loaded ? "Yükleniyor..." : term || zoneFilter ? "Filtrenizle eşleşen sonuç yok." : "Bu sekmede proje yok."}
                  </td>
                </tr>
              ) : (
                visible.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150" data-testid={`viewer-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold">{r.name}</td>
                    <td className="px-4 py-3 text-slate-500">{r.zone}</td>
                    <td className="px-4 py-3 text-slate-500">{r.stand_no}</td>
                    <td className="px-4 py-3 text-slate-500">{r.project_type}</td>
                    <td className="px-4 py-3"><StatusBadge r={r} /></td>
                    <td className="px-4 py-3" data-testid={`viewer-files-${r.id}`}>
                      {r.files?.length ? <FileChips files={r.files} /> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {r.winner_company ? (
                        <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-700"><Award size={13} /> {r.winner_company}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 tabular-nums">
                      {r.status === "won" ? fmtDate(r.date) : r.status === "active" && r.deadline ? `Son: ${fmtDate(r.deadline)}` : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={!!progressFor} onOpenChange={(o) => !o && setProgressFor(null)}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="stand-progress-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ListChecks size={18} /> {progressFor?.name}
            </DialogTitle>
          </DialogHeader>
          {progressFor && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-slate-500">Kurulum İlerlemesi</span>
                  <span className={`font-bold tabular-nums ${progressPct(progressFor) === 100 ? "text-emerald-600" : "text-slate-900"}`} data-testid="stand-progress-dialog-pct">
                    %{progressPct(progressFor)}
                  </span>
                </div>
                <div className="h-2 bg-slate-100 overflow-hidden">
                  <div className={`h-full transition-all duration-300 ${progressPct(progressFor) === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${progressPct(progressFor)}%` }} />
                </div>
              </div>
              <div className="space-y-1 max-h-[55vh] overflow-y-auto">
                {PROGRESS_ITEMS.map((item, i) => {
                  const checked = (progressFor.kurulum_progress || []).includes(item);
                  return (
                    <label
                      key={item}
                      className={`flex items-center gap-3 px-3 py-2 border cursor-pointer transition-colors duration-150 ${checked ? "bg-emerald-50/60 border-emerald-200" : "bg-white border-slate-200 hover:border-slate-300"}`}
                      data-testid={`stand-progress-item-${i}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleProgress(progressFor, item)} className="rounded-none" data-testid={`stand-progress-check-${i}`} />
                      <span className={`text-sm ${checked ? "text-slate-800 font-medium" : "text-slate-600"}`}>{item}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
